import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import { GeneralMeetingExportDialogComponent } from '../../../components/dialogs/general-meeting-export-dialog/general-meeting-export-dialog.component';
import { GeneralMeetingImportDialogComponent } from '../../../components/dialogs/general-meeting-import-dialog/general-meeting-import-dialog.component';
import { speedDialButtons } from '../../../components/layouts/speed-dial-fab/speed-dial-fab.component';
import { ButtonMapper } from '../../../core/utils/mappers/button.mapper';
import { BasicDataModel } from '../../../models/basic-data.model';
import { OptionalType } from '../../../models/types/optional.type';
import { ActionButtonsService } from '../../../services/action-buttons.service';
import { BasicDataService } from '../../../services/basic-data.service';
import { BasicDataResource } from '../../../services/resources/basic-data.resource';

@Component({
    selector: 'sl-general-settings',
    templateUrl: './general-settings.component.html',
    styleUrls: ['./general-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GeneralSettingsComponent implements OnInit {
    readonly isLoading = signal(true);
    readonly currentData = signal<OptionalType<BasicDataModel>>(undefined);

    readonly formGroup = new FormGroup({
        // settings netvote
        netvoteShowYesInstruction: new FormControl(false),
        netvoteShowNoInstruction: new FormControl(false),
        netvoteShowAbstentionInstruction: new FormControl(false),
        netvoteShowBodAgreeInstruction: new FormControl(false),
        netvoteShowBodDisagreeInstruction: new FormControl(false),
        netvoteUseWordingForCooperatives: new FormControl(false),
        netvote2FADisabled: new FormControl(false),
        mobileConfirmationDisabled: new FormControl(false),
        netvoteCredentialPrefix: new FormControl('', [Validators.minLength(3), Validators.maxLength(3)]),
        netvoteInstructionText: new FormControl(''),
        encourageElectronicDeliveryMethod: new FormControl(false),

        // ui
        netvoteBackgroundUrl: new FormControl(''),
        netvotePrimaryColor: new FormControl(''),
        languageDe: new FormControl(false),
        languageEn: new FormControl(false),
        languageFr: new FormControl(false),
        languageIt: new FormControl(false),

        // general meeting
        allowWebRegistrationSelf: new FormControl(false),
        allowElectronicAdmissionCard: new FormControl(false),
        allowWebRegistrationIndependentProxy: new FormControl(false),
        allowWebAttendance: new FormControl(false)
    });

    private readonly baseButtons = [ButtonMapper.save.withAction(() => this.save())];
    private readonly basicDataResource = inject(BasicDataResource);
    private readonly basicDataService = inject(BasicDataService);
    private readonly actionButtonsService = inject(ActionButtonsService);
    private readonly dialog = inject(MatDialog);

    ngOnInit(): void {
        speedDialButtons.set(this.baseButtons);

        // do not get basic data from service (cached), since we need the related actions
        this.basicDataResource.get().subscribe(resp => {
            if (!!resp) {
                this.currentData.set(resp.value);
                this.isLoading.set(false);

                this.formGroup.patchValue(resp.value);
                this.formGroup.markAsPristine();

                if (resp.relatedActions && resp.relatedActions.length > 0) {
                    speedDialButtons.update(curr => [...curr, ...this.actionButtonsService.convert(resp.relatedActions)]);
                }
            }
        });
    }

    save(): void {
        this.formGroup.updateValueAndValidity();
        this.formGroup.markAllAsTouched();

        if (this.formGroup.invalid) {
            return;
        }

        this.isLoading.set(true);

        const current = this.currentData();
        const formValue = this.formGroup.getRawValue();
        const updated: BasicDataModel = {
            ...current!,
            netvoteShowYesInstruction: !!formValue.netvoteShowYesInstruction,
            netvoteShowNoInstruction: !!formValue.netvoteShowNoInstruction,
            netvoteShowAbstentionInstruction: !!formValue.netvoteShowAbstentionInstruction,
            netvoteShowBodAgreeInstruction: !!formValue.netvoteShowBodAgreeInstruction,
            netvoteShowBodDisagreeInstruction: !!formValue.netvoteShowBodDisagreeInstruction,
            netvoteUseWordingForCooperatives: !!formValue.netvoteUseWordingForCooperatives,
            netvote2FADisabled: !!formValue.netvote2FADisabled,
            mobileConfirmationDisabled: !!formValue.mobileConfirmationDisabled,
            netvoteCredentialPrefix: formValue.netvoteCredentialPrefix?.toUpperCase() || '',
            netvoteInstructionText: formValue.netvoteInstructionText || '',
            encourageElectronicDeliveryMethod: !!formValue.encourageElectronicDeliveryMethod,
            netvoteBackgroundUrl: formValue.netvoteBackgroundUrl || undefined,
            netvotePrimaryColor: formValue.netvotePrimaryColor || undefined,
            languageDe: !!formValue.languageDe,
            languageEn: !!formValue.languageEn,
            languageFr: !!formValue.languageFr,
            languageIt: !!formValue.languageIt,
            allowWebRegistrationSelf: !!formValue.allowWebRegistrationSelf,
            allowElectronicAdmissionCard: !!formValue.allowElectronicAdmissionCard,
            allowWebRegistrationIndependentProxy: !!formValue.allowWebRegistrationIndependentProxy,
            allowWebAttendance: !!formValue.allowWebAttendance
        };

        this.currentData.set(updated);

        // using the service, so the cache gets cleared
        this.basicDataService.update(updated).subscribe({
            complete: () => this.isLoading.set(false)
        });
    }

    exportGm(): void {
        this.dialog.open(GeneralMeetingExportDialogComponent, { disableClose: true });
    }

    importGm(): void {
        this.dialog.open(GeneralMeetingImportDialogComponent, { disableClose: true, closeOnNavigation: false });
    }
}
